import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Upload, Server, CheckCircle, XCircle, Loader2, 
  Globe, Lock, FolderOpen, ArrowLeft, Shield
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DeployPage() {
  const [credentials, setCredentials] = useState({
    host: "",
    username: "",
    password: "",
    port: 21,
    remote_path: "/public_html",
    protocol: "ftp"
  });
  
  const [status, setStatus] = useState({
    testing: false,
    testResult: null,
    deploying: false,
    deployResult: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: name === "port" ? parseInt(value) || 21 : value
    }));
    
    // Update default port based on protocol
    if (name === "protocol") {
      setCredentials(prev => ({
        ...prev,
        port: value === "sftp" ? 22 : 21
      }));
    }
  };

  const testConnection = async () => {
    setStatus(prev => ({ ...prev, testing: true, testResult: null }));
    try {
      const response = await axios.post(`${API}/deploy/test-connection`, credentials);
      setStatus(prev => ({ ...prev, testing: false, testResult: response.data }));
    } catch (error) {
      setStatus(prev => ({ 
        ...prev, 
        testing: false, 
        testResult: { success: false, message: error.message } 
      }));
    }
  };

  const deployWebsite = async () => {
    setStatus(prev => ({ ...prev, deploying: true, deployResult: null }));
    try {
      const response = await axios.post(`${API}/deploy/upload`, credentials);
      setStatus(prev => ({ ...prev, deploying: false, deployResult: response.data }));
    } catch (error) {
      setStatus(prev => ({ 
        ...prev, 
        deploying: false, 
        deployResult: { success: false, message: error.message } 
      }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1220] py-32">
      <div className="max-w-2xl mx-auto px-6">
        <Link 
          to="/" 
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={18} /> Back to Website
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#D9F845] flex items-center justify-center">
              <Upload className="text-[#0A1220]" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0A1220]">Deploy Website</h1>
              <p className="text-[#64748B] text-sm">Transfer files to your hosting account</p>
            </div>
          </div>

          <div className="bg-[#FEF3C7] border border-[#F59E0B] p-4 mb-6">
            <div className="flex items-start gap-2">
              <Shield className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-[#92400E]">
                <strong>Security Note:</strong> Your credentials are used only for this transfer and are not stored.
              </p>
            </div>
          </div>

          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            {/* Protocol Selection */}
            <div>
              <label className="block text-sm font-medium text-[#0A1220] mb-2">
                Transfer Protocol
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="protocol"
                    value="ftp"
                    checked={credentials.protocol === "ftp"}
                    onChange={handleChange}
                    className="accent-[#D9F845]"
                    data-testid="protocol-ftp"
                  />
                  <span className="text-[#0A1220]">FTP</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="protocol"
                    value="sftp"
                    checked={credentials.protocol === "sftp"}
                    onChange={handleChange}
                    className="accent-[#D9F845]"
                    data-testid="protocol-sftp"
                  />
                  <span className="text-[#0A1220]">SFTP (SSH)</span>
                </label>
              </div>
            </div>

            {/* Host & Port */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-[#0A1220] mb-2">
                  <Globe size={14} className="inline mr-1" /> Host / Server
                </label>
                <input
                  type="text"
                  name="host"
                  value={credentials.host}
                  onChange={handleChange}
                  placeholder="ftp.yourdomain.com"
                  className="form-input"
                  data-testid="input-host"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A1220] mb-2">
                  Port
                </label>
                <input
                  type="number"
                  name="port"
                  value={credentials.port}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="input-port"
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-[#0A1220] mb-2">
                <Server size={14} className="inline mr-1" /> Username
              </label>
              <input
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                placeholder="your_ftp_username"
                className="form-input"
                data-testid="input-username"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#0A1220] mb-2">
                <Lock size={14} className="inline mr-1" /> Password
              </label>
              <input
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="form-input"
                data-testid="input-password"
                required
              />
            </div>

            {/* Remote Path */}
            <div>
              <label className="block text-sm font-medium text-[#0A1220] mb-2">
                <FolderOpen size={14} className="inline mr-1" /> Remote Directory
              </label>
              <input
                type="text"
                name="remote_path"
                value={credentials.remote_path}
                onChange={handleChange}
                placeholder="/public_html"
                className="form-input"
                data-testid="input-remote-path"
              />
              <p className="text-xs text-[#64748B] mt-1">
                Usually /public_html for cPanel or /var/www/html for servers
              </p>
            </div>

            {/* Test Result */}
            {status.testResult && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 ${status.testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}
                data-testid="test-result"
              >
                <div className="flex items-center gap-2">
                  {status.testResult.success ? (
                    <CheckCircle className="text-green-600" size={18} />
                  ) : (
                    <XCircle className="text-red-600" size={18} />
                  )}
                  <span className={status.testResult.success ? 'text-green-800' : 'text-red-800'}>
                    {status.testResult.message}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Deploy Result */}
            {status.deployResult && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 ${status.deployResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}
                data-testid="deploy-result"
              >
                <div className="flex items-start gap-2">
                  {status.deployResult.success ? (
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                  ) : (
                    <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  )}
                  <div>
                    <span className={status.deployResult.success ? 'text-green-800' : 'text-red-800'}>
                      {status.deployResult.message}
                    </span>
                    {status.deployResult.files_transferred > 0 && (
                      <p className="text-sm text-green-600 mt-1">
                        {status.deployResult.files_transferred} files transferred
                      </p>
                    )}
                    {status.deployResult.errors?.length > 0 && (
                      <ul className="text-sm text-red-600 mt-2 list-disc pl-4">
                        {status.deployResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={testConnection}
                disabled={status.testing || !credentials.host || !credentials.username || !credentials.password}
                className="btn-secondary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-test-connection"
              >
                {status.testing ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Server size={18} />
                )}
                {status.testing ? "Testing..." : "Test Connection"}
              </button>
              
              <button
                type="button"
                onClick={deployWebsite}
                disabled={status.deploying || !credentials.host || !credentials.username || !credentials.password}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-deploy"
              >
                {status.deploying ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Upload size={18} />
                )}
                {status.deploying ? "Deploying..." : "Deploy Website"}
              </button>
            </div>
          </form>
        </motion.div>

        <p className="text-center text-white/40 text-sm mt-6">
          This will build and upload the ICC Bahamas website to your hosting account.
        </p>
      </div>
    </div>
  );
}

export default DeployPage;
